import { Globe, Search } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languages, getLanguageByCode, getPopularLanguages, searchLanguages } from '../../i18n/languages';
import type { Language } from '../../i18n/languages';
import { getLanguageDirection } from '../../i18n/config';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import LanguageMenuItem from './LanguageMenuItem';

interface LanguageSwitcherUIProps {
  variant?: 'button' | 'compact';
  showLabel?: boolean;
  onLanguageChange: (languageCode: string) => Promise<void>;
  disabled?: boolean;
}

const LanguageSwitcherUI: React.FC<LanguageSwitcherUIProps> = ({ 
  variant = 'button', 
  showLabel = true, 
  onLanguageChange, 
  disabled = false
}) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const currentLanguage = getLanguageByCode(i18n.language);
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  const handleLanguageSelect = (languageCode: string) => {
    setOpen(false);
    setTimeout(async () => {
      try {
        await onLanguageChange(languageCode);
      } catch (error) {
        console.error('Failed to change language:', error);
      }
    }, 150);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchQuery('');
      setShowAllLanguages(false);
    }
  };

  // Filter languages based on search and popularity
  const getDisplayLanguages = (): { 
    popular: Language[]; 
    other: Language[]; 
    regions?: { [key: string]: Language[] } 
  } => {
    if (searchQuery.trim()) {
      return { popular: searchLanguages(searchQuery), other: [] };
    }

    if (!showAllLanguages) {
      return { popular: getPopularLanguages(), other: [] };
    }

    const popular = getPopularLanguages();
    const other = languages.filter(lang => !lang.popular);
    
    const regions = other.reduce((acc, lang) => {
      const region = lang.region || 'other';
      if (!acc[region]) acc[region] = [];
      acc[region].push(lang);
      return acc;
    }, {} as { [key: string]: Language[] });

    return { popular, other: [], regions };
  };

  const { popular, regions } = getDisplayLanguages();

  const triggerButton = variant === 'compact' ? (
    <Button 
      variant="ghost" 
      size="sm" 
      disabled={disabled}
      className="h-8 px-2 text-xs gap-1"
    >
      <Globe className="w-4 h-4" />
      <span>{currentLanguage.flag} {currentLanguage.code.toUpperCase()}</span>
    </Button>
  ) : (
    <Button 
      variant="ghost" 
      disabled={disabled}
      className="gap-2"
    >
      <Globe className="w-5 h-5" />
      {showLabel && <span>{t('components.languageSwitcher.language')}</span>}
      <span>{currentLanguage.flag} {currentLanguage.name}</span>
    </Button>
  );

  return (
    <div dir={getLanguageDirection(i18n.language)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {triggerButton}
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-0" 
          dir={getLanguageDirection(i18n.language)}
        >
          {/* Search Box */}
          <div className="p-4 pb-2">
            <div className="relative">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-4 h-4 text-muted-foreground`} />
              <Input
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-9' : 'pl-9'}`}
              />
            </div>
          </div>

          <ScrollArea className="h-[400px]">
            {/* Popular Languages */}
            {!searchQuery && popular.length > 0 && (
              <div className="px-4 pb-2">
                <Badge variant="secondary" className="text-xs">
                  {t('components.languageSwitcher.popular')}
                </Badge>
                <div className="mt-2 space-y-1">
                  {popular.map((lang) => (
                    <LanguageMenuItem
                      key={lang.code}
                      language={lang}
                      isSelected={lang.code === currentLanguage.code}
                      isRTL={isRTL}
                      onSelect={handleLanguageSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {searchQuery && popular.length > 0 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-muted-foreground mb-2">
                  {t('components.languageSwitcher.searchResults')} ({popular.length})
                </p>
                <div className="space-y-1">
                  {popular.map((lang) => (
                    <LanguageMenuItem
                      key={lang.code}
                      language={lang}
                      isSelected={lang.code === currentLanguage.code}
                      isRTL={isRTL}
                      onSelect={handleLanguageSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Show All Toggle */}
            {!searchQuery && !showAllLanguages && (
              <>
                <Separator className="my-2" />
                <div className="px-2 pb-2">
                  <Button
                    variant="ghost"
                    onClick={() => setShowAllLanguages(true)}
                    className="w-full text-gold font-medium"
                  >
                    {t('components.languageSwitcher.showAll')} ({languages.length - popular.length} {t('common.languageSwitcher.more')})
                  </Button>
                </div>
              </>
            )}

            {/* All Languages by Region */}
            {!searchQuery && showAllLanguages && regions && (
              <>
                <Separator className="my-2" />
                {Object.entries(regions).map(([region, regionLanguages]) => (
                  <div key={region} className="px-4 pb-2">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">
                      {t(`components.languageSwitcher.regions.${region}`, 
                        region.charAt(0).toUpperCase() + region.slice(1))}
                    </p>
                    <div className="space-y-1">
                      {regionLanguages.map((lang) => (
                        <LanguageMenuItem
                          key={lang.code}
                          language={lang}
                          isSelected={lang.code === currentLanguage.code}
                          isRTL={isRTL}
                          onSelect={handleLanguageSelect}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* No Results */}
            {searchQuery && popular.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('components.languageSwitcher.noResults')}
                </p>
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default LanguageSwitcherUI;
